import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';
import { compose } from 'redux';

import type { W_Organization_CompaniesRevenueTable_VM_Row } from 'ab-api/web/organization/companiesRevenueTable/viewModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { RootState } from 'af-reducers';

import { dollarFormatter } from 'af-utils/format.util';

import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import Breadcrumbs from 'af-components/Breadcrumbs';
import type { TableRef } from 'af-components/Table';
import TableNew from 'af-components/Table';
import type { TableProps } from 'af-components/Table/types';

import CLIENT from 'af-routes/client';

import * as OrganizationActions from 'af-actions/organizations';

import { BREADCRUMBS } from './constants';
import { getYesterday } from './helpers';
import TableHeader from './TableHeader';
import type { HeaderTotals } from './types';

type Props = ConnectedProps<typeof connector>;

const Companies: React.FC<Props> = (props) => {

	const {
		findOrganizationCompaniesRevenueTable,
		findOrganizationCompaniesRevenueTableTotals,
		orgAlias,
	} = props;

	const yesterday = getYesterday();
	const breadcrumbs = getBreadcrumbs();

	const tableRef = React.useRef<TableRef<W_Organization_CompaniesRevenueTable_VM_Row>>(null);

	const navigate = useNavigate();

	const fetchRows = React.useCallback(async (tableRequestModel: TableQuery) => {
		const { page, pageSize } = tableRequestModel;
		const result = await findOrganizationCompaniesRevenueTable(yesterday, new TableQuery({ page, pageSize }));
		return result;
	}, [findOrganizationCompaniesRevenueTable, yesterday]);

	const handleFindOrganizationCompaniesRevenueTableTotals = React.useCallback(async () => {
		const result = await findOrganizationCompaniesRevenueTableTotals(yesterday);
		return { ...result };
	}, [findOrganizationCompaniesRevenueTableTotals, yesterday]);

	const renderTableHeader = React.useCallback((totals: HeaderTotals) => {
		return (
			<TableHeader
				cumulativeMonthlyBilling={totals.cumulativeMonthlyBilling}
				cumulativeMonthlySalesMarkup={totals.cumulativeMonthlySalesMarkup}
				fieldEmployees={totals.fieldEmployees}
				payrollDifferentialDeduction={totals.payrollDifferentialDeduction}
				revenuePerHour={totals.revenuePerHour}
				revenuePerPerson={totals.revenuePerPerson}
				totalEmployees={totals.totalEmployees}
				totalRevenue={totals.totalRevenue}
				totalSalesMarkup={totals.totalSalesMarkup}
			/>
		);
	}, []);
	const navigateToCompaniesRevenueNotificationSettingsPage = React.useCallback(async () => {
		if (!orgAlias) {
			return;
		}
		navigate(CLIENT.ORGANIZATION.COMPANIES_REVENUE_NOTIFICATION_SETTINGS(orgAlias));
	}, [navigate, orgAlias]);

	const buttons = React.useMemo(() => {
		return [{
			type: TableButtonType.PRIMARY,
			hasPermission: true,
			label: 'Manage Revenue Overview Notifications',
			onClick: navigateToCompaniesRevenueNotificationSettingsPage,
		}];
	}, [navigateToCompaniesRevenueNotificationSettingsPage]);

	const columns: TableProps<W_Organization_CompaniesRevenueTable_VM_Row>['columns'] = React.useMemo(() => {
		const _columns = [
			{
				id: 'companyName',
				header: 'Company Name',
				accessor: 'companyName',
				cell: (_cell: CellContext<W_Organization_CompaniesRevenueTable_VM_Row, string>) => _cell.row.original.companyName,
			},
			{
				id: 'employeeDistribution',
				header: '# Field Employees/Total Employees',
				accessor: 'employeeDistribution',
				cell: (_cell: CellContext<W_Organization_CompaniesRevenueTable_VM_Row, string>) => _employeeDistributionAccessor(_cell.row.original),
			},
			{
				id: 'totalRevenue',
				header: 'Total Daily Revenue',
				accessor: 'totalRevenue',
				cell: (_cell: CellContext<W_Organization_CompaniesRevenueTable_VM_Row, string>) => `${dollarFormatter.format(_cell.row.original.totalRevenue)}`,
			},
			{
				id: 'totalMaterialMarkup',
				header: 'Total Daily Material Markup',
				accessor: 'totalSalesMarkup',
				cell: (_cell: CellContext<W_Organization_CompaniesRevenueTable_VM_Row, string>) => `${dollarFormatter.format(_cell.row.original.totalSalesMarkup)}`,
			},
			{
				id: 'revenuePerPerson',
				header: 'Revenue Per Person',
				accessor: 'revenuePerPerson',
				cell: (_cell: CellContext<W_Organization_CompaniesRevenueTable_VM_Row, string>) => `${dollarFormatter.format(_cell.row.original.revenuePerPerson)}`,
			},
			{
				id: 'revenuePerHour',
				header: 'Revenue Per Hour',
				accessor: 'revenuePerHour',
				cell: (_cell: CellContext<W_Organization_CompaniesRevenueTable_VM_Row, string>) => `${dollarFormatter.format(_cell.row.original.revenuePerHour)}`,
			},
			{
				id: 'cumulativeMonthlyMaterialMarkup',
				header: 'Cumulative Monthly Material Markup',
				accessor: 'cumulativeMonthlySalesMarkup',
				cell: (_cell: CellContext<W_Organization_CompaniesRevenueTable_VM_Row, string>) => `${dollarFormatter.format(_cell.row.original.cumulativeMonthlySalesMarkup)}`,
			},
			{
				id: 'cumulativeMonthlyBilling',
				header: 'Cumulative Monthly Billing',
				accessor: 'cumulativeMonthlyBilling',
				cell: (_cell: CellContext<W_Organization_CompaniesRevenueTable_VM_Row, string>) => `${dollarFormatter.format(_cell.row.original.cumulativeMonthlyBilling)}`,
			},

		];

		if (process.env.FTD_DISABLE_PR_DIFF !== 'true') {
			_columns.push({
				id: 'payrollDifferentialDeduction',
				header: 'Cumulative P/R Diff',
				accessor: 'payrollDifferentialDeduction',
				cell: (_cell: CellContext<W_Organization_CompaniesRevenueTable_VM_Row, string>) =>
					`${dollarFormatter.format(+_cell.row.original.payrollDifferentialDeduction)}`,
			});
		}

		return _columns;
	}, []);

	return (
		<div>
			<Breadcrumbs items={breadcrumbs} />
			<TableNew
				buttons={buttons}
				columns={columns}
				defaultPageSize={100}
				fetch={fetchRows}
				fetchTableHeaderData={handleFindOrganizationCompaniesRevenueTableTotals}
				hasSubRows={false}
				hideActionHeader={false}
				ref={tableRef}
				renderTableHeader={renderTableHeader}
				searchLabel="Companies"
				selectable={true}
				tableName={TableNameEnum.ORGANIZATION_COMPANIES}
			/>
		</div>
	);

	function getBreadcrumbs() {
		const yesterdayFormatted = TimeUtils.formatDate(yesterday, TimeFormat.US_DATE);
		return [...BREADCRUMBS, { label: yesterdayFormatted }];
	}

	function _employeeDistributionAccessor(row: W_Organization_CompaniesRevenueTable_VM_Row) {
		return `${row.fieldEmployees}/${row.totalEmployees}`;
	}
};

function mapStateToProps(state: RootState) {
	return {
		orgAlias: state.user.organizationData?.alias ?? null,
	};
}

function mapDispatchToProps() {
	return {
		findOrganizationCompaniesRevenueTable: OrganizationActions.findOrganizationCompaniesRevenueTable,
		findOrganizationCompaniesRevenueTableTotals: OrganizationActions.findOrganizationCompaniesRevenueTableTotals,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType>(
	React.memo,
	connector
);

export default enhance(Companies);
